import { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useIsMobile } from '@/lib/useMediaQuery';
import { useQuery } from '@tanstack/react-query';
import { queries } from '@/data';
import { EmptyState } from '@/components/EmptyState';
import { useUnitTerms } from '@/features/map-core/useUnitTerms';
import { useFiltersStore } from './store';
import { presetsFromMeta, setCountryPresets } from './presets-config';
import { useFilteredComuni } from './useFilteredComuni';
import { useEmitFilterApplied } from './useEmitFilterApplied';
import { detectTopConstraint, relaxPatch } from './detect-constraint';
import type { Zone } from './types';
import { uiLang } from '@/i18n/lang';

/**
 * Контрол панели фильтров ↔ id слоя из `meta.filterable_fields`.
 *
 * Единственная таблица соответствия; следующей стране не нужно гадать, каким
 * словом называется поле — достаточно перечислить id слоёв в своём meta.json.
 *
 * Правило: список в meta задан и id в нём НЕТ ⇒ контрол не рендерится вовсе.
 * Не «неактивным» и не «с нулём»: фильтр по полю, которого нет в датасете,
 * отсекает все единицы разом (см. filter-fns.ts — неизвестное значение не
 * удовлетворяет активному фильтру), и пользователь видит пустую карту.
 *
 * Списка в meta нет ⇒ ограничений нет, рендерится всё (случай Италии).
 */
const CONTROL_FIELD = {
  /** Диапазон высоты (alt_min/alt_max). */
  altitude: 'altitude',
  /** Максимум июльской температуры. */
  tJulMax: 't_jul',
  /** Минимум январской температуры. */
  tJanMin: 't_jan',
  /** Максимум влажности. */
  humMax: 'hum',
  /** Минимум снежных дней. */
  snowMin: 'snow',
  /** Минимум солнечных дней. */
  sunnyMin: 'sunny',
  /** Чипы рельефа (zona altimetrica ISTAT у Италии). */
  zones: 'zone',
} as const;

function EmptyMatchHint() {
  const { t } = useTranslation();
  const terms = useUnitTerms();
  const { raw, set, reset } = useFiltersStore();
  const { data: meta } = useQuery(queries.meta());
  const { data: comuni } = useQuery({
    ...queries.full(meta?.version ?? ''),
    enabled: !!meta?.version,
  });
  const top = comuni ? detectTopConstraint(raw, comuni) : null;

  if (!top) {
    return (
      <EmptyState
        tone="warn"
        icon="🤷"
        title={t('filters.empty.title')}
        description={t('filters.empty.fallback')}
        action={{ label: t('filters.empty.resetAll'), onClick: reset }}
      />
    );
  }

  const pct = Math.round(top.ratio * 100);
  const constraintLabel = t(`constraints.${top.key}`, { defaultValue: top.label });
  return (
    <EmptyState
      tone="warn"
      icon="🎯"
      title={t('filters.empty.title')}
      description={
        <Trans
          i18nKey="filters.empty.constraintDesc"
          values={{ label: constraintLabel, pct, ...terms }}
          components={{ strong: <strong className="font-semibold" /> }}
        />
      }
      action={{
        label: t('filters.empty.relax', { label: constraintLabel }),
        onClick: () => set(relaxPatch(top.key)),
      }}
    />
  );
}

interface SingleSliderProps {
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  value: number | null;
  defaultEdge: 'min' | 'max';
  onChange: (v: number | null) => void;
}

function SingleSlider({ label, unit, min, max, step, value, defaultEdge, onChange }: SingleSliderProps) {
  const { t } = useTranslation();
  const isActive = value !== null;
  const displayValue = value ?? (defaultEdge === 'max' ? max : min);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-700">{label}</span>
        <button
          onClick={() => onChange(isActive ? null : displayValue)}
          className={`text-[10px] underline-offset-2 ${isActive ? 'text-blue-600' : 'text-slate-400'} hover:underline`}
        >
          {isActive ? `${displayValue} ${unit}` : t('filters.noFilterValue')}
        </button>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={displayValue}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1.5 w-full cursor-pointer accent-blue-600"
        disabled={false}
      />
    </div>
  );
}

interface RangeSliderProps {
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  valueMin: number;
  valueMax: number;
  onChange: (vmin: number, vmax: number) => void;
}

function RangeSlider({ label, unit, min, max, step, valueMin, valueMax, onChange }: RangeSliderProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-slate-700">
        <span>{label}</span>
        <span className="text-slate-500">
          {valueMin}–{valueMax} {unit}
        </span>
      </div>
      <div className="flex gap-1">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={valueMin}
          onChange={(e) => onChange(Math.min(Number(e.target.value), valueMax), valueMax)}
          className="h-1.5 w-full cursor-pointer accent-blue-600"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={valueMax}
          onChange={(e) => onChange(valueMin, Math.max(Number(e.target.value), valueMin))}
          className="h-1.5 w-full cursor-pointer accent-blue-600"
        />
      </div>
    </div>
  );
}

const ZONE_KEYS: Zone[] = [
  'pianura',
  'collina_interna',
  'collina_litoranea',
  'montagna_interna',
  'montagna_litoranea',
];

export function FiltersPanel() {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(isMobile);
  const { raw, presetActive, set, reset, applyPreset } = useFiltersStore();
  const { matched, total, filterActive, isLoading } = useFilteredComuni();
  const { data: meta } = useQuery(queries.meta());
  const terms = useUnitTerms();
  useEmitFilterApplied();

  const isRu = uiLang(i18n.language) === 'ru';
  const presets = useMemo(() => presetsFromMeta(meta), [meta]);

  // Тот же список отдаём стору фильтров: он применяет пресет по id из URL,
  // когда панель ещё не смонтирована, и не может подписаться на React Query.
  useEffect(() => {
    setCountryPresets(presets);
    const store = useFiltersStore.getState();
    const active = store.presetActive;
    if (!active) return;
    if (presets.some((p) => p.id === active)) {
      // Пресет из ссылки не мог примениться на монтировании — список страны
      // тогда ещё не загрузился. Добираем его здесь, без второго события
      // аналитики: пользователь ничего не нажимал.
      store.applyPreset(active, { silent: true });
    } else {
      // id из чужой страны («Доломиты» в ссылке на Испанию). Фильтры он не
      // задавал — снимаем только подсветку чипа.
      useFiltersStore.setState({ presetActive: null });
    }
  }, [presets]);

  // Поля, по которым у страны есть данные. undefined ⇒ ограничений нет.
  const fields = meta?.filterable_fields;
  const hasField = (field: string) => !fields || fields.includes(field);

  const numFmt = new Intl.NumberFormat(i18n.language);

  // Esc закрывает раскрытую панель
  useEffect(() => {
    if (collapsed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCollapsed(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [collapsed]);

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="absolute left-2 top-16 z-[1000] inline-flex min-h-[36px] items-center rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-slate-700 shadow-md backdrop-blur-sm hover:bg-white sm:left-4 sm:top-[16.5rem]"
        title={t('filters.open')}
        aria-label={t('filters.open')}
      >
        ☰ {t('filters.title')}
        {filterActive && (
          <span
            className={`ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold ${
              matched === 0
                ? 'bg-red-100 text-red-700'
                : 'bg-blue-100 text-blue-700'
            }`}
            aria-label={
              matched === 0
                ? t('filters.matchedZeroAria', {
                    ...terms,
                    defaultValue: 'No {{unitPlural}} match current filters',
                  })
                : t('filters.matchedAria', { count: matched, defaultValue: '{{count}} matched' })
            }
          >
            {matched}
          </span>
        )}
      </button>
    );
  }

  const toggleZone = (z: Zone) => {
    const newZones = new Set(raw.zones);
    if (newZones.has(z)) newZones.delete(z);
    else newZones.add(z);
    set({ zones: newZones });
  };

  return (
    <>
      {/* Mobile backdrop */}
      <div
        onClick={() => setCollapsed(true)}
        className="fixed inset-0 z-[1009] bg-black/30 sm:hidden"
        aria-hidden="true"
      />
      <aside className="absolute left-2 right-2 top-16 z-[1010] flex max-h-[calc(100dvh-5rem)] flex-col overflow-hidden rounded-lg bg-white/95 shadow-lg backdrop-blur-sm sm:left-4 sm:right-auto sm:top-[16.5rem] sm:z-[1000] sm:max-h-[calc(100dvh-21.5rem)] sm:w-72">
      {/* Non-scrolling header */}
      <div className="flex shrink-0 flex-col gap-2 p-2.5 pb-1">
        <div className="flex items-center justify-between">
          <div
            className="text-sm font-semibold text-slate-800"
            aria-live="polite"
            aria-atomic="true"
          >
            {isLoading ? (
              <span className="text-slate-400">{t('loading.default')}</span>
            ) : filterActive ? (
              <>
                {t('filters.matched')}{' '}
                <span className={matched === 0 ? 'text-red-600' : 'text-blue-600'}>
                  {numFmt.format(matched)}
                </span>{' '}
                / {numFmt.format(total)}
              </>
            ) : (
              t('filters.totalCount', { count: total, ...terms })
            )}
          </div>
          <button
            onClick={() => setCollapsed(true)}
            className="flex h-9 w-9 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            title={t('filters.close')}
            aria-label={t('filters.close')}
          >
            ✕
          </button>
        </div>

        {/* Presets */}
        <div className="flex flex-wrap gap-1">
          {presets.map((p) => {
            // Пресеты из meta приходят с подписями на двух языках; у встроенных
            // подпись живёт в локали, а p.label — фолбэк.
            const label = t(`presets.${p.id}`, {
              defaultValue: (isRu && p.labelRu) || p.label,
            });
            return (
            <button
              key={p.id}
              onClick={() => applyPreset(p.id)}
              className={`inline-flex min-h-[26px] items-center rounded-full border px-2 py-0.5 text-[11px] transition ${
                presetActive === p.id
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
              title={label}
            >
              <span className="mr-1">{p.emoji}</span>
              {label}
            </button>
            );
          })}
        </div>
      </div>

      {/* Scrollable body: sliders + zones + empty state */}
      <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-1">
        {/* Sliders */}
        <div className="flex flex-col gap-1.5 border-t border-slate-100 pt-1.5">
          {hasField(CONTROL_FIELD.altitude) && (
            <RangeSlider
              label={t('filters.altitude')}
              unit="м"
              min={0}
              max={3500}
              step={50}
              valueMin={raw.alt_min}
              valueMax={raw.alt_max}
              onChange={(alt_min, alt_max) => set({ alt_min, alt_max })}
            />
          )}
          {hasField(CONTROL_FIELD.tJulMax) && (
            <SingleSlider
              label={t('filters.tJulMax')}
              unit="°C"
              min={10}
              max={35}
              step={1}
              value={raw.t_jul_max}
              defaultEdge="max"
              onChange={(t_jul_max) => set({ t_jul_max })}
            />
          )}
          {hasField(CONTROL_FIELD.tJanMin) && (
            <SingleSlider
              label={t('filters.tJanMin')}
              unit="°C"
              min={-15}
              max={10}
              step={1}
              value={raw.t_jan_min}
              defaultEdge="min"
              onChange={(t_jan_min) => set({ t_jan_min })}
            />
          )}
          {hasField(CONTROL_FIELD.humMax) && (
            <SingleSlider
              label={t('filters.humMax')}
              unit="%"
              min={40}
              max={95}
              step={1}
              value={raw.hum_max}
              defaultEdge="max"
              onChange={(hum_max) => set({ hum_max })}
            />
          )}
          {hasField(CONTROL_FIELD.snowMin) && (
            <SingleSlider
              label={t('filters.snowMin')}
              unit="дн"
              min={0}
              max={120}
              step={5}
              value={raw.snow_min}
              defaultEdge="min"
              onChange={(snow_min) => set({ snow_min })}
            />
          )}
          {hasField(CONTROL_FIELD.sunnyMin) && (
            <SingleSlider
              label={t('filters.sunnyMin')}
              unit="дн"
              min={80}
              max={300}
              step={10}
              value={raw.sunny_min}
              defaultEdge="min"
              onChange={(sunny_min) => set({ sunny_min })}
            />
          )}
        </div>

        {/* Zones */}
        {hasField(CONTROL_FIELD.zones) && (
          <div className="border-t border-slate-100 pt-2">
            <div className="mb-1 text-xs text-slate-700">{t('filters.zonesTitle')}</div>
            <div className="flex flex-wrap gap-1">
              {ZONE_KEYS.map((z) => (
                <button
                  key={z}
                  onClick={() => toggleZone(z)}
                  className={`inline-flex min-h-[32px] items-center rounded border px-2 py-1 text-[11px] ${
                    raw.zones.has(z)
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                  }`}
                >
                  {t(`zones.${z}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Empty state (inside scroll area so long hints don't overflow) */}
        {filterActive && matched === 0 && !isLoading && <EmptyMatchHint />}
      </div>

      {/* Sticky footer: reset button, always visible when filters are active */}
      {filterActive && (
        <div className="shrink-0 border-t border-slate-100 p-2.5 pt-2">
          <button
            onClick={reset}
            className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            {t('filters.reset')}
          </button>
        </div>
      )}
    </aside>
    </>
  );
}
